import { Fragment, useState } from 'react';
import {
  exportMixdown,
  exportStems,
  DEFAULT_EXPORT_SETTINGS,
  LOUDNESS_TARGETS,
  type ExportSettings,
} from '@/engine/export';
import { exportProjectPackage, importProjectPackage, PACKAGE_EXTENSION } from '@/storage/packageIO';
import {
  AUDIO_ACCEPT,
  MIDI_ACCEPT,
  PROJECT_ACCEPT,
  downloadBlob,
  pickFiles,
  pickSaveTarget,
  writeFile,
  FileCancelled,
} from '@/storage/fileIO';
import { getServices } from '@/state/services';
import { describeError, formatBytes, useDaw } from '@/state/store';

export function Modals(): JSX.Element | null {
  const modal = useDaw((s) => s.modal);
  if (modal === 'none') return null;
  return (
    <div className="overlay" role="dialog" aria-modal="true" onPointerDown={(e) => {
      if (e.target === e.currentTarget) useDaw.getState().setModal('none');
    }}>
      {modal === 'projects' && <ProjectsModal />}
      {modal === 'export' && <ExportModal />}
      {modal === 'shortcuts' && <ShortcutsModal />}
    </div>
  );
}

function ModalShell({
  title,
  children,
  footer,
}: {
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="modal">
      <div className="modal__head">
        <span className="modal__title">{title}</span>
        <button className="btn btn--ghost" onClick={() => useDaw.getState().setModal('none')} aria-label="Close">
          ✕
        </button>
      </div>
      <div className="modal__body">{children}</div>
      {footer && <div className="modal__foot">{footer}</div>}
    </div>
  );
}

function ProjectsModal(): JSX.Element {
  const projects = useDaw((s) => s.projectList);
  const currentId = useDaw((s) => s.project.meta.id);
  const [busy, setBusy] = useState<string | null>(null);

  const exportPackage = async (): Promise<void> => {
    const store = useDaw.getState();
    const services = getServices();
    setBusy('Packing project…');
    try {
      await store.captureWamStates();
      const blob = await exportProjectPackage(useDaw.getState().project, services.storage);
      const name = `${store.project.meta.name.replace(/[\\/:*?"<>|]/g, '-')}${PACKAGE_EXTENSION}`;
      try {
        const target = await pickSaveTarget(name, 'Stage Zero project', { 'application/zip': [PACKAGE_EXTENSION] });
        await writeFile(target, blob);
      } catch (error) {
        if (error instanceof FileCancelled) return;
        downloadBlob(blob, name);
      }
      store.notify({ kind: 'success', title: 'Project package written', detail: `${formatBytes(blob.size)} including all audio.`, sticky: false });
    } catch (error) {
      store.notify({ kind: 'error', title: 'Export failed', detail: describeError(error), sticky: true });
    } finally {
      setBusy(null);
    }
  };

  const importPackage = async (): Promise<void> => {
    const store = useDaw.getState();
    const services = getServices();
    let files: File[] = [];
    try {
      files = await pickFiles('Stage Zero project', PROJECT_ACCEPT, false);
    } catch {
      return;
    }
    if (files.length === 0) return;

    setBusy('Reading package…');
    try {
      const { project, warnings } = await importProjectPackage(files[0], services.storage);
      await services.storage.saveProject(project);
      await store.refreshProjectList();
      await store.openProject(project.meta.id);
      store.notify({
        kind: warnings.length > 0 ? 'warning' : 'success',
        title: `Imported "${project.meta.name}"`,
        detail: warnings.length > 0 ? warnings.slice(0, 2).join(' · ') : undefined,
        sticky: warnings.length > 0,
      });
    } catch (error) {
      store.notify({ kind: 'error', title: 'Import failed', detail: describeError(error), sticky: true });
    } finally {
      setBusy(null);
    }
  };

  return (
    <ModalShell
      title="Projects on this computer"
      footer={
        <>
          <span style={{ flex: 1, color: 'var(--text-3)', alignSelf: 'center' }}>
            {busy ?? 'Projects are stored in this browser. Export a package to move one elsewhere.'}
          </span>
          <button className="btn" onClick={() => void importPackage()}>
            Import package…
          </button>
          <button className="btn" onClick={() => void exportPackage()}>
            Export package…
          </button>
          <button className="btn btn--primary" onClick={() => void useDaw.getState().newProject()}>
            New project
          </button>
        </>
      }
    >
      {projects.length === 0 && <div className="empty-note">No saved projects yet. Save the current one to keep it.</div>}
      {projects.map((summary) => (
        <div className="project-row" key={summary.id}>
          <div>
            <div style={{ fontWeight: 540 }}>
              {summary.name}
              {summary.id === currentId && <span style={{ color: 'var(--text-3)' }}> — open</span>}
            </div>
            <div className="project-row__meta">
              {summary.trackCount} tracks · {summary.clipCount} clips · {formatBytes(summary.sizeBytes)} ·{' '}
              {new Date(summary.modifiedAt).toLocaleString()}
            </div>
          </div>
          <button className="btn" onClick={() => void useDaw.getState().duplicateProject(summary.id)}>
            Duplicate
          </button>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn" onClick={() => void useDaw.getState().openProject(summary.id)} disabled={summary.id === currentId}>
              Open
            </button>
            <button
              className="btn btn--danger"
              onClick={() => {
                if (window.confirm(`Delete "${summary.name}"? This cannot be undone.`)) {
                  void useDaw.getState().deleteProject(summary.id);
                }
              }}
            >
              Delete
            </button>
          </div>
        </div>
      ))}
    </ModalShell>
  );
}

function ExportModal(): JSX.Element {
  const [settings, setSettings] = useState<ExportSettings>(DEFAULT_EXPORT_SETTINGS);
  const [progress, setProgress] = useState<string | null>(null);
  const [measured, setMeasured] = useState<string | null>(null);
  const project = useDaw((s) => s.project);

  const run = async (stems: boolean): Promise<void> => {
    const store = useDaw.getState();
    await store.activateAudio();
    const assets = getServices().assets();
    if (!assets) return;

    setProgress('Preparing…');
    try {
      // The engine's stretch cache is shared with the export, so audio already
      // stretched for playback is not stretched again on the way out.
      const stretches = getServices().engine.stretchCache;
      const files = stems
        ? await exportStems(project, assets, settings, (u) => setProgress(u.label), stretches)
        : [await exportMixdown(project, assets, settings, (u) => setProgress(u.label), stretches)];

      for (const file of files) {
        try {
          const target = await pickSaveTarget(file.name, `${settings.format.toUpperCase()} audio`, {
            [settings.format === 'wav' ? 'audio/wav' : 'audio/mpeg']: [`.${settings.format}`],
          });
          await writeFile(target, file.blob);
        } catch (error) {
          if (error instanceof FileCancelled) break;
          downloadBlob(file.blob, file.name);
        }
      }

      // What is reported is what the files measure, not what was asked for: a
      // target that could not be reached without clipping is not silently met.
      const loudest = files.reduce((a, b) => (b.loudness.integratedLufs > a.loudness.integratedLufs ? b : a));
      const { integratedLufs, truePeakDb, loudnessRangeLu } = loudest.loudness;
      const short = Number.isFinite(integratedLufs) ? `${integratedLufs.toFixed(1)} LUFS` : 'silent';
      setMeasured(
        `${short} · true peak ${Number.isFinite(truePeakDb) ? truePeakDb.toFixed(1) : '-∞'} dBTP · range ${loudnessRangeLu.toFixed(1)} LU${
          loudest.limiterGainReductionDb > 0.05 ? ` · limited ${loudest.limiterGainReductionDb.toFixed(1)} dB` : ''
        }`,
      );

      const clipping = truePeakDb > 0;
      store.notify({
        kind: clipping ? 'warning' : 'success',
        title: files.length === 1 ? 'Mix exported' : `${files.length} stems exported`,
        detail: loudest.loudnessLimitedByPeak
          ? `${short}, short of the target: going louder would have clipped it. Turn the limiter on to reach it.`
          : `${short}, true peak ${truePeakDb.toFixed(1)} dBTP${
              loudest.limiterGainReductionDb > 0.05
                ? `, ${loudest.limiterGainReductionDb.toFixed(1)} dB of limiting`
                : ''
            }${clipping ? ' — this will clip' : ''}.`,
        sticky: clipping || loudest.loudnessLimitedByPeak,
      });
      if (!clipping && !loudest.loudnessLimitedByPeak) store.setModal('none');
    } catch (error) {
      store.notify({ kind: 'error', title: 'Export failed', detail: describeError(error), sticky: true });
    } finally {
      setProgress(null);
    }
  };

  return (
    <ModalShell
      title="Export audio"
      footer={
        <>
          <span style={{ flex: 1, color: 'var(--text-3)', alignSelf: 'center' }}>
            {progress ?? 'Rendered offline through the same signal path you hear.'}
          </span>
          <button
            className="btn"
            disabled={progress !== null}
            onClick={() => void useDaw.getState().exportMidiFile()}
            title="Write the project's instrument parts as a Standard MIDI File"
          >
            Export MIDI
          </button>
          <button className="btn" disabled={progress !== null} onClick={() => void run(true)}>
            Export stems
          </button>
          <button className="btn btn--primary" disabled={progress !== null} onClick={() => void run(false)}>
            Export mix
          </button>
        </>
      }
    >
      <div className="inspector" style={{ padding: 0 }}>
        <div className="inspector__group">
          <div className="inspector__title">Format</div>
          <div className="inspector__row">
            <span className="inspector__label">File type</span>
            <select
              className="select"
              value={settings.format}
              onChange={(e) => setSettings({ ...settings, format: e.target.value as ExportSettings['format'] })}
            >
              <option value="wav">WAV (lossless)</option>
              <option value="mp3">MP3</option>
            </select>
            <span />
          </div>
          {settings.format === 'wav' ? (
            <div className="inspector__row">
              <span className="inspector__label">Bit depth</span>
              <select
                className="select"
                value={settings.bitDepth}
                onChange={(e) => setSettings({ ...settings, bitDepth: Number(e.target.value) as ExportSettings['bitDepth'] })}
              >
                <option value={16}>16-bit</option>
                <option value={24}>24-bit</option>
                <option value={32}>32-bit float</option>
              </select>
              <span />
            </div>
          ) : (
            <div className="inspector__row">
              <span className="inspector__label">Bitrate</span>
              <select
                className="select"
                value={settings.mp3Bitrate}
                onChange={(e) => setSettings({ ...settings, mp3Bitrate: Number(e.target.value) })}
              >
                {[128, 192, 256, 320].map((rate) => (
                  <option key={rate} value={rate}>
                    {rate} kbps
                  </option>
                ))}
              </select>
              <span />
            </div>
          )}
          <div className="inspector__row">
            <span className="inspector__label">Sample rate</span>
            <select
              className="select"
              value={settings.sampleRate}
              onChange={(e) => setSettings({ ...settings, sampleRate: Number(e.target.value) })}
            >
              {[44100, 48000, 96000].map((rate) => (
                <option key={rate} value={rate}>
                  {rate} Hz
                </option>
              ))}
            </select>
            <span />
          </div>
        </div>

        <div className="inspector__group">
          <div className="inspector__title">Range</div>
          <div className="inspector__row">
            <span className="inspector__label">Export</span>
            <select
              className="select"
              value={settings.range}
              onChange={(e) => setSettings({ ...settings, range: e.target.value as ExportSettings['range'] })}
            >
              <option value="project">Whole arrangement</option>
              <option value="loop">Loop region only</option>
            </select>
            <span />
          </div>
          <div className="inspector__row">
            <span className="inspector__label">Tail</span>
            <input
              className="slider"
              type="range"
              min={0}
              max={10}
              step={0.5}
              value={settings.tailSec}
              onChange={(e) => setSettings({ ...settings, tailSec: Number(e.target.value) })}
            />
            <span className="inspector__readout">{settings.tailSec.toFixed(1)} s</span>
          </div>
          <div className="inspector__row">
            <span className="inspector__label">Level</span>
            <select
              className="numeric"
              style={{ gridColumn: '2 / 4', cursor: 'pointer' }}
              aria-label="Output level"
              title="Leave the mix as it is, make its loudest sample touch the ceiling, or bring it to a loudness target."
              value={settings.targetLufs === null ? (settings.normalise ? 'peak' : 'off') : String(settings.targetLufs)}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'off') setSettings({ ...settings, normalise: false, targetLufs: null });
                else if (value === 'peak') setSettings({ ...settings, normalise: true, targetLufs: null });
                else setSettings({ ...settings, normalise: false, targetLufs: Number(value) });
              }}
            >
              <option value="off">As mixed</option>
              <option value="peak">Peak, to -0.3 dBFS</option>
              {LOUDNESS_TARGETS.map((target) => (
                <option key={target.lufs} value={target.lufs}>
                  {target.label}
                </option>
              ))}
            </select>
          </div>

          {settings.targetLufs !== null && (
            <div className="inspector__row">
              <span className="inspector__label">Limiter</span>
              <input
                type="checkbox"
                checked={settings.limitOnExport}
                onChange={(e) => setSettings({ ...settings, limitOnExport: e.target.checked })}
                aria-label="Limit peaks to reach the target"
              />
              <span className="inspector__readout">
                {settings.limitOnExport ? 'to -1 dBTP' : 'off'}
              </span>
            </div>
          )}

          {measured && (
            <div className="inspector__row">
              <span className="inspector__label">Measured</span>
              <span className="inspector__readout" style={{ gridColumn: '2 / 4' }}>
                {measured}
              </span>
            </div>
          )}
        </div>
      </div>
    </ModalShell>
  );
}

const SHORTCUTS: [string, string][] = [
  ['Space', 'Play / pause'],
  ['R', 'Record on armed tracks'],
  ['S', 'Split clips at the playhead'],
  ['Delete / Backspace', 'Delete the selection'],
  ['Ctrl/Cmd + Z', 'Undo'],
  ['Ctrl/Cmd + Shift + Z', 'Redo'],
  ['Ctrl/Cmd + C / X / V', 'Copy, cut, paste'],
  ['Ctrl/Cmd + D', 'Duplicate the selection'],
  ['Ctrl/Cmd + A', 'Select all clips'],
  ['Ctrl/Cmd + S', 'Save the project'],
  ['Ctrl/Cmd + O', 'Open the project browser'],
  ['Ctrl/Cmd + E', 'Export audio'],
  ['Ctrl/Cmd + F', 'Crossfade the selection or the edit at the playhead'],
  ['Delete', 'Clear the selected range, leaving a gap'],
  ['Shift + Delete', 'Splice the range out, closing the gap'],
  ['Ctrl/Cmd + I', 'Insert time the length of the range'],
  ['Ctrl/Cmd + Shift + I', 'Insert time on every track'],
  ['L', 'Toggle loop'],
  ['K', 'Toggle metronome'],
  ['Home / End', 'Go to the start / end'],
  ['Ctrl/Cmd + scroll', 'Zoom the timeline'],
  ['Shift + scroll', 'Scroll horizontally'],
  ['Alt + drag on ruler', 'Set the loop region'],
];

function ShortcutsModal(): JSX.Element {
  return (
    <ModalShell title="Keyboard shortcuts">
      <div className="shortcut-grid">
        {SHORTCUTS.map(([keys, description]) => (
          <Fragment key={keys}>
            <kbd>{keys}</kbd>
            <span style={{ color: 'var(--text-1)' }}>{description}</span>
          </Fragment>
        ))}
      </div>
    </ModalShell>
  );
}

/** Import MIDI files through a file picker. */
export async function promptImportMidi(): Promise<void> {
  const store = useDaw.getState();
  try {
    const files = await pickFiles('MIDI files', MIDI_ACCEPT, true);
    if (files.length > 0) await store.importMidiFiles(files, store.project.transport.playheadTicks);
  } catch {
    /* dialog dismissed */
  }
}

/** Import audio through a file picker, used by the menu as well as drag and drop. */
export async function promptImportAudio(): Promise<void> {
  const store = useDaw.getState();
  try {
    const files = await pickFiles('Audio files', AUDIO_ACCEPT, true);
    if (files.length > 0) await store.importFiles(files, store.selection.trackId, store.project.transport.playheadTicks);
  } catch {
    /* dialog dismissed */
  }
}
